import React, { useCallback } from 'react';
import classnames from 'classnames';
import PhoneInput from 'react-phone-number-input/input-max';

import Input from '../../controls/input/Input';
import AdvancedSelect from '../../controls/advanced-select/AdvancedSelect';
import TextArea from '../../controls/text-area/TextArea';
import Switch from '../../controls/switch/Switch';
import PhotoUpload from '../../controls/photo-upload/PhotoUpload';
import BusinessHours from '../../business-hours/BusinessHours';
import Error from '../../error/Error';
import SpeedLimit from '../../speed-limit/SpeedLimit';
import Select from '../../controls/select/Select';
import RoutablePoints from '../../routable-points/RoutablePoints';
import Label from '../../controls/label/Label';
import Datepicker from '../../controls/date-picker/DatePicker';
import Button from '../../controls/button/Button';
import Clearance from '../../clearance/Clearance';

import './Field.scss';

export const FIELD_TYPES = {
  input: 'input',
  phoneInput: 'phoneInput',
  advancedSelect: 'advancedSelect',
  select: 'select',
  textArea: 'textArea',
  switch: 'switch',
  businessHours: 'businessHours',
  photos: 'photos',
  speedLimit: 'speedLimit',
  routablePoints: 'routablePoints',
  datepicker: 'datepicker',
  // this is just a container for a data, not a real field shown on the original form
  form: 'form',
  button: 'button',
  clearance: 'clearance',
};

export interface FieldProps {
  className?: string;
  field?;
  error?;
  label;
  type;
  hint?;
  required?: boolean;
  errorShownByControl?;
  controlProps?;
  disabled?: boolean;
  value;
  beforeChange?;
  onChange;
}

const Field = ({
  field,
  error,
  label,
  type,
  hint,
  required,
  errorShownByControl,
  className,
  controlProps = {},
  disabled,
  value,
  beforeChange,
  onChange,
}: FieldProps) => {
  const handleChange = useCallback(
    (value, path?) => {
      if (beforeChange) {
        value = beforeChange(value);
      }

      if (onChange) {
        onChange(field, value, path);
      }
    },
    [field, beforeChange, onChange],
  );

  const handleInputChange = useCallback(
    ({ target: { value } }) => handleChange(value),
    [handleChange],
  );

  return (
    <div className={classnames('field', className)}>
      {label && (
        <Label className="field__label" label={label} required={required} />
      )}
      {type === FIELD_TYPES.input && (
        <Input
          className="field__input"
          type="text"
          hasError={!!error}
          {...controlProps}
          disabled={disabled}
          value={value || ''}
          onChange={handleInputChange}
        />
      )}
      {type === FIELD_TYPES.phoneInput && (
        <PhoneInput
          className="field__input"
          type="text"
          hasError={!!error}
          {...controlProps}
          disabled={disabled}
          value={value || ''}
          onChange={handleChange}
          inputComponent={Input}
        />
      )}
      {type === FIELD_TYPES.advancedSelect && (
        <AdvancedSelect
          className="field__advanced-select"
          hasError={!!error}
          {...controlProps}
          disabled={disabled}
          value={value}
          onChange={handleChange}
        />
      )}
      {type === FIELD_TYPES.select && (
        <Select
          className="field__select"
          hasError={!!error}
          {...controlProps}
          disabled={disabled}
          value={value}
          onChange={handleChange}
        />
      )}
      {type === FIELD_TYPES.textArea && (
        <TextArea
          className="field__textarea"
          hasError={!!error}
          {...controlProps}
          disabled={disabled}
          value={value}
          onChange={handleChange}
        />
      )}
      {type === FIELD_TYPES.switch && (
        <Switch
          className="field__switch"
          hasError={!!error}
          {...controlProps}
          disabled={disabled}
          value={value}
          onChange={handleChange}
        />
      )}
      {type === FIELD_TYPES.businessHours && (
        <BusinessHours
          {...controlProps}
          disabled={disabled}
          value={value}
          onChange={handleChange}
        />
      )}
      {type === FIELD_TYPES.photos && (
        <PhotoUpload
          className="field__photo-upload"
          {...controlProps}
          disabled={disabled}
          value={value}
          onChange={handleChange}
        />
      )}
      {type === FIELD_TYPES.speedLimit && (
        <SpeedLimit
          className="field__speed-limit"
          hasError={!!error}
          {...controlProps}
          disabled={disabled}
          value={value}
          onChange={handleChange}
        />
      )}
      {type === FIELD_TYPES.routablePoints && (
        <RoutablePoints
          className="field__routable-points"
          {...controlProps}
          disabled={disabled}
          error={error}
          value={value}
          onChange={handleChange}
        />
      )}
      {type === FIELD_TYPES.datepicker && (
        <Datepicker
          className="field__date-picker"
          hasError={!!error}
          {...controlProps}
          disabled={disabled}
          value={value}
          onChange={handleChange}
        />
      )}
      {type === FIELD_TYPES.button && (
        <Button
          className="field__button"
          {...controlProps}
          disabled={disabled}
        />
      )}
      {type === FIELD_TYPES.clearance && (
        <Clearance
          className="field__clearance"
          hasError={!!error}
          {...controlProps}
          disabled={disabled}
          error={error}
          value={value}
          onChange={handleChange}
        />
      )}
      {!errorShownByControl && error && (
        <Error className="field__error" text={error} />
      )}
      {hint && <div className="field__hint">{hint}</div>}
    </div>
  );
};

export default Field;
